import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { dataService } from '../services/dataService';
import type { Category, Owner, Item, Review, Transaction } from '../types';

interface DataContextType {
  categories: Category[];
  loading: boolean;
  error: string | null;
  isUsingSupabaseData: boolean;
  refreshData: () => Promise<void>;
  getOwner: (ownerId: string) => Promise<Owner | null>;
  getUserItems: (userId: string) => Promise<Item[]>;
  getUserRentals: (userId: string) => Promise<any[]>;
  getUserNotifications: (userId: string) => Promise<any[]>;
  getUserReviews: (userId: string) => Promise<Review[]>;
  getUserTransactions: (userId: string) => Promise<Transaction[]>;
  addItem: (item: Item) => Promise<boolean>;
  deleteItem: (itemId: string) => Promise<boolean>;
  updateItem: (item: Item) => Promise<boolean>;
  uploadProfilePicture: (userId: string, file: File) => Promise<string | null>;
  getCurrentUser: () => Promise<any>;
  getUserData: (userId: string) => Promise<any>;
  refreshUserData: (userId: string) => Promise<any>;
  updateUserPhoneNumber: (userId: string, phoneNumber: any) => Promise<boolean>;
  refreshOwnersCache: () => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUsingSupabaseData, setIsUsingSupabaseData] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await dataService.getCategories();
      setCategories(data);
      setIsUsingSupabaseData(dataService.isUsingSupabaseData());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    try {
      setLoading(true);
      setError(null);
      await dataService.refreshData();
      const data = await dataService.getCategories();
      setCategories(data);
      setIsUsingSupabaseData(dataService.isUsingSupabaseData());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh data');
      console.error('Error refreshing data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getOwner = async (ownerId: string): Promise<Owner | null> => {
    return await dataService.getOwner(ownerId);
  };

  const getUserItems = async (userId: string): Promise<Item[]> => {
    return await dataService.getUserItems(userId);
  };

  const getUserRentals = async (userId: string): Promise<any[]> => {
    return await dataService.getUserRentals(userId);
  };

  const getUserNotifications = async (userId: string): Promise<any[]> => {
    return await dataService.getUserNotifications(userId);
  };

  const getUserReviews = async (userId: string): Promise<Review[]> => {
    return await dataService.getUserReviews(userId);
  };

  const getUserTransactions = async (userId: string): Promise<Transaction[]> => {
    return await dataService.getUserTransactions(userId);
  };

  const addItem = async (item: Item): Promise<boolean> => {
    return await dataService.addItem(item);
  };

  const deleteItem = async (itemId: string): Promise<boolean> => {
    return await dataService.deleteItem(itemId);
  };

  const updateItem = async (item: Item): Promise<boolean> => {
    return await dataService.updateItem(item);
  };

  const uploadProfilePicture = async (userId: string, file: File): Promise<string | null> => {
    return await dataService.uploadProfilePicture(userId, file);
  };

  const getCurrentUser = async () => {
    return await dataService.getCurrentUser();
  };

  const getUserData = async (userId: string) => {
    return await dataService.getUserData(userId);
  };

  const refreshUserData = async (userId: string) => {
    return await dataService.refreshUserData(userId);
  };

  const updateUserPhoneNumber = async (userId: string, phoneNumber: any): Promise<boolean> => {
    return await dataService.updateUserPhoneNumber(userId, phoneNumber);
  };

  const refreshOwnersCache = async () => {
    await dataService.refreshOwnersCache();
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <DataContext.Provider
      value={{
        categories,
        loading,
        error,
        isUsingSupabaseData,
        refreshData,
        getOwner,
        getUserItems,
        getUserRentals,
        getUserNotifications,
        getUserReviews,
        getUserTransactions,
        addItem,
        deleteItem,
        updateItem,
        uploadProfilePicture,
        getCurrentUser,
        getUserData,
        refreshUserData,
        updateUserPhoneNumber,
        refreshOwnersCache
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
}; 